import { Button } from 'rbx';
import * as React from 'react';
import { useMutation } from '@apollo/client';

import { BASKET_QUERY, BASKET_UPDATE_STATUS_MUTATION } from './../../../api/GraphQLQueries/Basket';
import IsAdminQuery from './../../auth/IsAdminQuery';
import { notification } from './../../../utils/notification';

interface IProps {
    basketId: string;
    basketStatus: string;
}

const BASKET_STATES = [
    {
        status: 'draft',
        nextStatus: 'submitted',
        text: 'Submit Basket',
    },
    {
        status: 'submitted',
        nextStatus: 'locked',
        text: 'Lock Basket',
    },
    {
        status: 'locked',
        nextStatus: 'quoted',
        text: 'Request Quotation',
    },
    {
        status: 'quoted',
        nextStatus: 'completed',
        text: 'Complete',
    },
];

const BasketStatusMutation: React.FC<IProps> = ({ basketId, basketStatus }) => {

    const [updateStatus, { loading }] = useMutation(BASKET_UPDATE_STATUS_MUTATION, {
        onCompleted: () => {
            notification.success('Successfully updated basket status');
        },
        onError: () => {
            notification.error('Basket status update error');
        },
        refetchQueries: basketId
            ? [{ query: BASKET_QUERY, variables: { id: basketId } }]
            : [],
    });

    const getNextStatus = () => {
        return BASKET_STATES.find(states => states.status === basketStatus);
    };

    const handleUpdateBasketStatus = (newStatus: string) => () => {
        updateStatus({ variables: { id: basketId, status: newStatus } });
    };

    const renderMutationAction = () => {
        if (!basketId) return null;

        const statusHandler = getNextStatus();

        if (statusHandler) {
            return (
                <Button
                    color="primary"
                    data-cy="basketStatusMutation-submit-btn"
                    state={loading ? 'loading' : undefined}
                    disabled={loading}
                    onClick={handleUpdateBasketStatus(statusHandler.nextStatus)}
                >
                    {statusHandler.text}
                </Button>
            );
        }
        return null;
    };

    const renderAdminStatusUpdateMutation = () => {
        return basketStatus !== 'completed' ? renderMutationAction() : null;
    };

    const renderUserStatusUpdateMutation = () => {
        return basketStatus === 'draft' ? renderMutationAction() : null;
    };

    if (!basketId || !basketStatus) return null;

    return (
        <IsAdminQuery
            renderAdmin={renderAdminStatusUpdateMutation}
            renderUser={renderUserStatusUpdateMutation}
        />
    );
};

export default BasketStatusMutation;
